import { useState, useMemo } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'
import { Search, Building2, AlertCircle, CheckCircle2, Loader2, Users } from 'lucide-react'
import { DebouncedInput } from '@/components/DebouncedInput'
import { useResellerCoverageItemsQuery } from '@/queries/useResellerCoverageReportQuery'
import { ResellerCoverageItem } from '@/api/business/marketResearch'

interface ResellerCoverageItemsDialogProps {
    isOpen: boolean
    onClose: () => void
    resellerData: ResellerCoverageItem | null
}

const ResellerCoverageItemsDialog = ({ isOpen, onClose, resellerData }: ResellerCoverageItemsDialogProps) => {
    const [searchTerm, setSearchTerm] = useState('')

    const payload = useMemo(() => {
        if (!resellerData || !resellerData.SK_PESQUISA || !resellerData.SK_REVENDA) {
            return null
        }
        return {
            SK_PESQUISA: resellerData.SK_PESQUISA,
            SK_REVENDA: resellerData.SK_REVENDA,
        }
    }, [resellerData])

    const { data: items, isLoading } = useResellerCoverageItemsQuery(payload)

    const filteredItems = useMemo(() => {
        if (!items) return []
        if (!searchTerm.trim()) return items

        const term = searchTerm.toLowerCase()
        return items.filter((item) => {
            const sellerMatch = item.NM_FUNCIONARIO?.toLowerCase().includes(term)
            const clientMatch = item.DS_NOME_FANTASIA?.toLowerCase().includes(term)
            const productMatch = item.DS_PRODUTO?.toLowerCase().includes(term)
            const statusMatch = item.DD_STATUS?.toLowerCase().includes(term)
            const reasonMatch = item.DS_MOTIVO_NAO_PESQUISA?.toLowerCase().includes(term)
            return sellerMatch || clientMatch || productMatch || statusMatch || reasonMatch
        })
    }, [items, searchTerm])

    // Summary stats
    const stats = useMemo(() => {
        if (!items) return { totalItems: 0, totalClients: 0, totalSellers: 0, nonResearchCount: 0 }
        const uniqueClients = new Set(items.map((i) => i.SK_CLIENTE)).size
        const uniqueSellers = new Set(items.map((i) => i.SK_VENDEDOR)).size
        const nonResearch = items.filter((i) => Boolean(i.DS_MOTIVO_NAO_PESQUISA)).length
        return {
            totalItems: items.length,
            totalClients: uniqueClients,
            totalSellers: uniqueSellers,
            nonResearchCount: nonResearch,
        }
    }, [items])

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col p-0 overflow-hidden" aria-describedby={undefined}>
                {/* Header */}
                <DialogHeader className="p-5 border-b bg-gray-50 flex flex-col gap-1">
                    <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        Detalhamento de Pesquisa por Revenda
                    </DialogTitle>
                    {resellerData && (
                        <DialogDescription className="text-sm text-gray-600">
                            Revenda: <span className="font-semibold text-gray-900">{resellerData.DS_REVENDA}</span> • Pesquisa: <span className="font-semibold text-gray-900">{resellerData.NM_PESQUISA}</span>
                        </DialogDescription>
                    )}
                </DialogHeader>

                {/* Subheader / Summary Stats */}
                <div className="p-4 bg-white border-b flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 font-semibold">
                            <Users className="w-4 h-4" />
                            <span>{stats.totalSellers} Vendedores</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 font-semibold">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{stats.totalClients} Clientes Atendidos</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 font-semibold">
                            <Building2 className="w-4 h-4" />
                            <span>{stats.totalItems} Produtos Pesquisados</span>
                        </div>
                        {stats.nonResearchCount > 0 && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 font-semibold">
                                <AlertCircle className="w-4 h-4" />
                                <span>{stats.nonResearchCount} Não Pesquisados</span>
                            </div>
                        )}
                    </div>

                    <div className="relative w-full sm:w-64">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <DebouncedInput
                            value={searchTerm}
                            onChange={(val) => setSearchTerm(String(val))}
                            placeholder="Buscar vendedor, cliente ou produto..."
                            className="pl-9 w-full bg-gray-50 text-xs h-9 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                    </div>
                </div>

                {/* Body Table */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            <p className="text-sm font-medium">Carregando dados da revenda...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            Nenhum item encontrado para esta revenda e pesquisa.
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className="bg-gray-100/90 text-gray-700 font-semibold border-b">
                                        <th className="p-3 pl-4">Vendedor</th>
                                        <th className="p-3">Cliente</th>
                                        <th className="p-3">Produto</th>
                                        <th className="p-3 text-right">Preço Compra</th>
                                        <th className="p-3 text-center">Volume</th>
                                        <th className="p-3 text-right">Preço Venda</th>
                                        <th className="p-3">Status / Motivo</th>
                                        <th className="p-3 pr-4 text-right">Data</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredItems.map((item, idx) => {
                                        const isNonResearch = Boolean(item.DS_MOTIVO_NAO_PESQUISA)
                                        return (
                                            <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                                <td className="p-3 pl-4 font-semibold text-gray-900">
                                                    {item.NM_FUNCIONARIO || '-'}
                                                </td>
                                                <td className="p-3 font-medium text-gray-800">
                                                    {item.DS_NOME_FANTASIA}
                                                </td>
                                                <td className="p-3 font-medium text-gray-800">
                                                    {item.DS_PRODUTO}
                                                </td>
                                                <td className="p-3 text-right font-mono text-gray-700">
                                                    {isNonResearch
                                                        ? '-'
                                                        : item.VL_PRECO_COMPRA !== null
                                                        ? `R$ ${Number(item.VL_PRECO_COMPRA).toFixed(2)}`
                                                        : '-'}
                                                </td>
                                                <td className="p-3 text-center font-mono text-gray-700">
                                                    {isNonResearch ? '-' : item.QTD_VOLUME_COMPRA ?? '-'}
                                                </td>
                                                <td className="p-3 text-right font-mono text-gray-700">
                                                    {isNonResearch
                                                        ? '-'
                                                        : item.VL_PRECO_VENDA !== null
                                                        ? `R$ ${Number(item.VL_PRECO_VENDA).toFixed(2)}`
                                                        : '-'}
                                                </td>
                                                <td className="p-3">
                                                    {isNonResearch ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">
                                                            <AlertCircle className="w-3 h-3" />
                                                            {item.DS_MOTIVO_NAO_PESQUISA}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                                            <CheckCircle2 className="w-3 h-3" />
                                                            {item.DD_STATUS || 'Publicado'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="p-3 pr-4 text-right text-gray-500 font-mono">
                                                    {item.DT_PUBLICACAO
                                                        ? new Date(item.DT_PUBLICACAO).toLocaleDateString('pt-BR')
                                                        : item.CREATED_AT
                                                        ? new Date(item.CREATED_AT).toLocaleDateString('pt-BR')
                                                        : '-'}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default ResellerCoverageItemsDialog
